// Dr.Rx™ Role Calibration & Clinical Consultation Export Modal
import React from 'react';
import type { ChatSettings, ProfessionalFocus, LlmModel, ChatSession } from '../types';
import { useToast } from '../contexts/ToastContext';

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const ExportIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;

interface ChatSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    settings: ChatSettings;
    onSettingsChange: (newSettings: ChatSettings) => void;
    onClearHistory: () => void;
    sessions?: ChatSession[];
}

const PROFESSIONAL_FOCUSES: { id: ProfessionalFocus; label: string; desc: string }[] = [
    { id: 'Pharmacist', label: 'Pharmacist', desc: 'PK/PD, renal/hepatic dosing, CYP pathways, stability & TDM' },
    { id: 'Physician', label: 'Physician', desc: 'Guideline-directed therapy (GDMT), trial endpoints & differential' },
    { id: 'Nurse', label: 'Nurse', desc: 'IV administration rates, Y-site compatibility & bedside monitoring' },
    { id: 'Researcher', label: 'Researcher', desc: 'Receptor pharmacology, biostatistics & study methodology' },
];

const LLM_OPTIONS_DATA: { id: LlmModel; name: string; enabled: boolean }[] = [
    { id: 'gemini-3.8-flash', name: 'Gemini 3.8 Flash (Clinical Default & Recommended)', enabled: true },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash (Ultra-Low Latency)', enabled: true },
    { id: 'gemini-flash-latest', name: 'Gemini Flash (Auto-Updated)', enabled: true }
];
const LLM_OPTIONS = LLM_OPTIONS_DATA.filter(model => model.enabled);

const ChatSettingsModal: React.FC<ChatSettingsModalProps> = ({
    isOpen,
    onClose,
    settings,
    onSettingsChange,
    onClearHistory,
    sessions = []
}) => {
    const { showToast } = useToast();

    if (!isOpen) return null;

    const handleFocusChange = (focus: ProfessionalFocus) => {
        onSettingsChange({ ...settings, professionalFocus: focus });
    };

    const handleLlmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onSettingsChange({ ...settings, llmModel: e.target.value as LlmModel });
    };

    const handleExportAllChats = () => {
        if (!sessions || sessions.length === 0) {
            showToast('No clinical consultations available to export.', 'warning');
            return;
        }

        const lines: string[] = [
            '# Dr.Rx™ Clinical Consultation Archive',
            `Exported: ${new Date().toLocaleString()}`,
            `Clinician Lens: ${settings.professionalFocus}`,
            '---',
            ''
        ];

        sessions.forEach((session, idx) => {
            lines.push(`## ${idx + 1}. ${session.title} ${session.isPinned ? '[PINNED]' : ''}`);
            lines.push(`Date: ${new Date(session.createdAt).toLocaleString()}`);
            lines.push('');
            session.messages.forEach(m => {
                const speaker = m.role === 'user' ? 'Clinician Query' : 'Dr.Rx™ Clinical Co-Pilot';
                lines.push(`**${speaker}:**`);
                lines.push(m.text);
                lines.push('');
            });
            lines.push('---');
            lines.push('');
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `DrRx_Clinical_Consultations_${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showToast(`Exported ${sessions.length} clinical consultation(s) to Markdown.`, 'success');
    };

    return (
        <div onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50 page-enter-active" role="dialog" aria-modal="true">
            <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] rounded-2xl border border-blue-900/60 w-full max-w-lg text-gray-300 p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto custom-scrollbar">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-colors"><CloseIcon /></button>
                <h2 className="text-2xl font-bold text-white mb-1">Clinical Co-Pilot Settings</h2>
                <p className="text-xs text-gray-400 mb-6">Calibrate clinical depth, model engine, and consultation archives.</p>
                
                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">Active Clinician Focus</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {PROFESSIONAL_FOCUSES.map(item => {
                                const active = settings.professionalFocus === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => handleFocusChange(item.id)}
                                        className={`text-left p-3 rounded-xl border transition-all ${
                                            active
                                                ? 'bg-yellow-400/15 border-yellow-400 text-white shadow-md'
                                                : 'bg-[#101D35] border-blue-900/50 text-gray-300 hover:border-blue-700'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`font-bold text-sm ${active ? 'text-yellow-300' : 'text-white'}`}>{item.label}</span>
                                            {active && <span className="w-2 h-2 rounded-full bg-yellow-400" />}
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1 leading-snug">{item.desc}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="llmModel" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Clinical Reasoning Engine</label>
                        <select
                            id="llmModel"
                            value={settings.llmModel}
                            onChange={handleLlmChange}
                            className="w-full bg-[#101D35] border border-[#3A4A67] rounded-xl p-3 text-white font-medium focus:outline-none focus:ring-1 focus:ring-yellow-400"
                        >
                            {LLM_OPTIONS.map(model => (
                                <option key={model.id} value={model.id}>
                                    {model.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">Consultation Archive</h3>
                        <div className="space-y-2.5">
                            <button
                                type="button"
                                onClick={handleExportAllChats}
                                className="w-full flex items-center p-3.5 bg-[#101D35] hover:bg-blue-900/40 border border-blue-800/60 rounded-xl text-white font-semibold transition-colors text-left"
                            >
                                <ExportIcon /> Export All Consultations (.md)
                            </button>
                            <button
                                type="button"
                                onClick={onClearHistory}
                                className="w-full flex items-center p-3.5 bg-red-950/40 hover:bg-red-900/60 border border-red-800/50 rounded-xl text-red-200 font-semibold transition-colors text-left"
                            >
                                <TrashIcon /> Clear All Consultation History
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-8 text-right">
                    <button onClick={onClose} className="bg-yellow-400 text-gray-950 font-bold py-2.5 px-6 rounded-xl hover:bg-yellow-300 transition-all shadow-md">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatSettingsModal;